import { useTranslation } from '@crew/modules/i18n'
import { useModal } from 'components/layouts/modal/useModal'
import { FC, memo, useCallback, useMemo } from 'react'
import MaterialSymbolsCrown from '~icons/material-symbols/crown'
import { CrewNavigateToPaidPlanDialog } from '../crewNavigateToPaidPlanDialog/crewNavigateToPaidPlanDialog'
import classNames from 'classnames'
import { CrewButton } from 'components/elements/crewButton/crewButton'

type CrewPaidPlanFeatureButtonProps = {
  /** 有償機能が使えるかどうか */
  isPaidFeatureEnabled: boolean
  /** 有償機能試用期間中かどうか */
  isPaidFeatureTrial: boolean

  // コンポーネント内で強制する必要のあるpropsは、型から除外しておく
  // もしsizeを変更できるようにしたい場合は、アイコンのサイズも調整できるように修正すること
} & Omit<
  React.ComponentProps<typeof CrewButton>,
  'size' | 'icon' | 'iconPosition'
>

const CROWN_ICON_WIDTH = 20
const CROWN_ICON_HEIGHT = 20

/**
 * 有償プラン限定機能を表示するボタンコンポーネント
 * フリープランの場合は、クリックで有償プランへの誘導ダイアログを表示する
 * 有償プランの場合は、通常通りonClickが呼ばれる
 */
export const CrewPaidPlanFeatureButton: FC<CrewPaidPlanFeatureButtonProps> =
  memo((props) => {
    // propsから、直接指定する必要のないものを分割する
    const {
      isPaidFeatureEnabled,
      isPaidFeatureTrial,
      onClick,
      className,
      ...rest
    } = props
    const { t } = useTranslation()

    // 有償プランへの誘導ダイアログ
    const [
      isNavigateToPaidPlanDialogOpen,
      openNavigateToPaidPlanDialog,
      closeNavigateToPaidPlanDialog,
    ] = useModal()

    // クリック時の処理
    const handleCrewPaidPlanFeatureButtonClick = useCallback(() => {
      if (!isPaidFeatureEnabled && !isPaidFeatureTrial) {
        // 有償機能が使えない＆試用期間外の場合はダイアログを開く
        openNavigateToPaidPlanDialog()
      } else {
        // 有償プランの場合は通常の処理を実行
        onClick?.()
      }
    }, [
      isPaidFeatureEnabled,
      isPaidFeatureTrial,
      onClick,
      openNavigateToPaidPlanDialog,
    ])

    // アイコンのレンダリング
    const renderIcon = useMemo(() => {
      if (!isPaidFeatureEnabled || isPaidFeatureTrial) {
        return (
          <div className="flex" title={t('label.onlyForPaidPlan')}>
            <MaterialSymbolsCrown
              className="text-crew-yellow-3-light dark:text-crew-yellow-3-dark"
              width={CROWN_ICON_WIDTH}
              height={CROWN_ICON_HEIGHT}
            />
          </div>
        )
      }
      return null
    }, [isPaidFeatureEnabled, isPaidFeatureTrial, t])

    return (
      <>
        <CrewButton
          {...rest} // propsのうち、分割されたもの以外を展開する
          // 以下は強制する必要があるため、明示的に指定して上書きする
          className={classNames(
            {
              'opacity-50': !isPaidFeatureEnabled, // 有償機能が使えない場合は半透明にする
            },
            className // 外部からのクラス名も適用する
          )}
          onClick={handleCrewPaidPlanFeatureButtonClick}
          icon={renderIcon}
          iconPosition="right"
        />

        {/* 有償プランへの誘導ダイアログ */}
        <CrewNavigateToPaidPlanDialog
          isOpen={isNavigateToPaidPlanDialogOpen}
          onClose={closeNavigateToPaidPlanDialog}
        />
      </>
    )
  })
